/**
 * URL state management using nuqs for type-safe URL search params.
 * Enables shareable links, browser history integration, and persistent view state.
 */

import type { SortingState } from "@tanstack/react-table";
import { parseAsInteger, parseAsString, parseAsStringEnum, useQueryStates } from "nuqs";

// Tab type for list views
export type TabType = "invoices" | "drafts" | "corrections" | "proforma";

// Payment status filter options
export type PaymentStatus = "all" | "paid" | "unpaid";

// Individual parsers for flexibility and reusability
export const tabParser = parseAsStringEnum<TabType>(["invoices", "drafts", "corrections", "proforma"]).withDefault(
  "invoices",
);
export const pageParser = parseAsInteger.withDefault(0);
export const registryParser = parseAsString;
export const searchParser = parseAsString.withDefault("");
export const statusParser = parseAsStringEnum<PaymentStatus>(["all", "paid", "unpaid"]).withDefault("all");
export const dateParser = parseAsString;
export const amountParser = parseAsInteger;
export const sortParser = parseAsString;
// Preview modal state - selected invoice or draft ID for shareable links
export const invoiceParser = parseAsString;
export const draftParser = parseAsString;

// Combined state config for list views
export const listUrlStateConfig = {
  tab: tabParser,
  page: pageParser,
  registry: registryParser,
  q: searchParser,
  status: statusParser,
  from: dateParser,
  to: dateParser,
  min: amountParser,
  max: amountParser,
  sort: sortParser,
  invoice: invoiceParser,
  draft: draftParser,
};

// Type for URL state (inferred from config with defaults applied)
export type ListUrlState = {
  tab: TabType;
  page: number;
  registry: string | null;
  q: string;
  status: PaymentStatus;
  from: string | null;
  to: string | null;
  min: number | null;
  max: number | null;
  sort: string | null;
  invoice: string | null;
  draft: string | null;
};

// Type for setting URL state (all fields optional for partial updates)
export type SetListUrlState = (
  values: Partial<{
    tab: TabType | null;
    page: number | null;
    registry: string | null;
    q: string | null;
    status: PaymentStatus | null;
    from: string | null;
    to: string | null;
    min: number | null;
    max: number | null;
    sort: string | null;
    invoice: string | null;
    draft: string | null;
  }>,
) => void;

// Options for URL state management
// Using 'push' history to enable browser back/forward navigation for meaningful state changes
export const urlStateOptions = {
  history: "push" as const,
};

/**
 * Hook for managing URL-based list view state.
 * Returns current state and a setter function for partial updates.
 * Uses 'push' history mode to enable browser back/forward navigation.
 */
export function useListUrlState() {
  return useQueryStates(listUrlStateConfig, urlStateOptions);
}

/**
 * Parse sort string from URL to TanStack Table SortingState.
 * Format: "field:direction" (e.g., "issued:desc")
 */
export function parseSortString(sortStr: string | null): SortingState {
  if (!sortStr) {
    return [];
  }
  const parts = sortStr.split(":");
  if (parts.length !== 2) {
    return [];
  }
  const [field, direction] = parts;
  if (!field || (direction !== "asc" && direction !== "desc")) {
    return [];
  }
  return [{ id: field, desc: direction === "desc" }];
}

/**
 * Convert TanStack Table SortingState to URL sort string.
 * Returns null if no sorting (removes param from URL).
 */
export function stringifySortState(sorting: SortingState): string | null {
  if (sorting.length === 0) {
    return null;
  }
  const { id, desc } = sorting[0];
  return `${id}:${desc ? "desc" : "asc"}`;
}

/**
 * Convert URL filter state to InvoiceFilterValues format.
 */
export function urlStateToFilters(urlState: ListUrlState): {
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  status: PaymentStatus;
} {
  return {
    dateFrom: urlState.from ?? "",
    dateTo: urlState.to ?? "",
    amountMin: urlState.min?.toString() ?? "",
    amountMax: urlState.max?.toString() ?? "",
    status: urlState.status,
  };
}

/**
 * Check if any filters are active (excluding defaults).
 */
export function hasActiveFilters(urlState: ListUrlState): boolean {
  return (
    urlState.from !== null ||
    urlState.to !== null ||
    urlState.min !== null ||
    urlState.max !== null ||
    urlState.status !== "all"
  );
}
